import {
	forwardRef,
	useEffect,
	useLayoutEffect,
	useRef,
	HTMLAttributes,
} from "react";
import "quill-mention/dist/quill.mention.css";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import * as emojiMart from "emoji-mart";
import Delta from "quill-delta";
import QuillInner from "quill";
import { Mention } from "quill-mention";
import type { ScrollBlot, EmbedBlot } from "parchment";
const Embed = QuillInner.import("blots/embed") as typeof EmbedBlot;

(
	QuillInner.import("formats/link") as { PROTOCOL_WHITELIST: string[] }
).PROTOCOL_WHITELIST = ["http", "https", "mailto", "tel", "sms", "xmpp"];

export const Quill = forwardRef(
	(
		{
			readOnly,
			defaultValue,
			onTextChange,
			onSelectionChange,
			triggerRecentEdit,
			enterIsSend,
			toolbar,
			placeholder,
			mentionHandler,
			mentionClose,
			attachFiles,
			...rest
		}: HTMLAttributes<HTMLFormElement> & {
			readOnly?: boolean;
			defaultValue?: Delta;
			onTextChange?: (delta: Delta, oldContents: Delta, source: string) => void;
			onSelectionChange?: (
				selection: { index: number; length: number },
				oldSelection: { index: number; length: number },
				source: string,
			) => void;
			triggerRecentEdit?: () => void;
			enterIsSend?: false | (() => void);
			toolbar?: boolean;
			placeholder?: string;
			mentionHandler?: (
				searchTerm: string,
				renderList: (
					matches: {
						id: string;
						value: string;
						[key: string]: string | undefined;
					}[],
					searchTerm?: string,
				) => void,
				mentionChar: string,
			) => void;
			mentionClose: () => boolean;
			attachFiles: (files: File[]) => Promise<void>;
		},
		fwdRef,
	) => {
		const containerRef = useRef<HTMLFormElement>(null);
		const defaultValueRef = useRef(defaultValue);
		const onTextChangeRef = useRef(onTextChange);
		const onSelectionChangeRef = useRef(onSelectionChange);
		const attachFilesRef = useRef(attachFiles);
		const mentionHandlerRef = useRef(mentionHandler);
		const mentionCloseRef = useRef(mentionClose);
		const triggerRecentEditRef = useRef(triggerRecentEdit);
		const enterIsSendRef = useRef(enterIsSend);
		const toolbarRef = useRef(toolbar);
		const ref = useRef<QuillInner | null>(null);

		useLayoutEffect(() => {
			onTextChangeRef.current = onTextChange;
			onSelectionChangeRef.current = onSelectionChange;
			attachFilesRef.current = attachFiles;
			toolbarRef.current = toolbar;
			triggerRecentEditRef.current = triggerRecentEdit;
			enterIsSendRef.current = enterIsSend;
			mentionHandlerRef.current = mentionHandler;
			mentionCloseRef.current = mentionClose;
		});

		useEffect(() => {
			ref.current?.enable(!readOnly);
		}, [ref, readOnly]);

		useEffect(() => {
			const container = containerRef.current!;
			const editorContainer = container.appendChild(
				container.ownerDocument.createElement("div"),
			);

			const inlineFormat =
				(char: string, prefix: RegExp, format: string) =>
				(range: { index: number }, context: { prefix: string }) => {
					const matchLen = context.prefix.match(prefix)?.join("")?.length || 0;
					const [, offset] = quill.getLine(range.index);
					// Insert the text and cutoff history so that undo will get back
					// the raw value the user typed
					quill.insertText(range.index, char, QuillInner.sources.USER);
					quill.history.cutoff();
					const delta = new Delta()
						.retain(range.index - offset) // Everything before this line
						.retain(offset - matchLen) // this line before the match
						.delete(1)
						.retain(matchLen - 1, { [format]: true })
						.delete(1);
					quill.updateContents(delta, QuillInner.sources.USER);
					quill.history.cutoff();
					quill.setSelection(range.index - 1, QuillInner.sources.SILENT);
					quill.format(format, false);
					return false;
				};

			const autolink = (
				range: { index: number },
				context: { prefix: string; event: { key: string } },
			) => {
				const match = context.prefix.match(/https:\/\/[^\s>]+/)?.join("") || "";
				const matchLen = match.length;
				const [, offset] = quill.getLine(range.index);
				quill.history.cutoff();
				const delta = new Delta()
					.retain(range.index - offset) // Everything before this line
					.retain(offset - matchLen) // this line before the match
					.retain(matchLen, { link: match });
				quill.updateContents(delta, QuillInner.sources.USER);
				quill.history.cutoff();
				quill.setSelection(range.index + 1, QuillInner.sources.SILENT);
			};

			const quill = new QuillInner(editorContainer, {
				modules: {
					clipboard: true,
					history: true,
					toolbar: toolbarRef.current
						? [
								["bold", "italic", "strike", "link"],
								["blockquote", "code-block"], // formula requires katex
								["clean"],
							]
						: false,
					uploader: {
						mimetypes: ["image/png", "image/jpeg"],
						handler: (range: Range, files: File[]) => {
							const small = files.filter((file) => file.size <= 128000);
							const large = files.filter((file) => file.size > 128000);

							if (small.length > 0) {
								(
									quill.uploader.constructor as unknown as {
										DEFAULTS: {
											handler: (range: Range, files: File[]) => void;
										};
									}
								).DEFAULTS.handler.call(quill.uploader, range, small);
							}

							if (large.length > 0) attachFilesRef.current(large);
						},
					},
					keyboard: {
						bindings: {
							enter: {
								key: "Enter",
								shiftKey: false,
								collapsed: true,
								handler: (
									range: { index: number },
									context: { prefix: string; event: { key: string } },
								) => {
									if (enterIsSendRef.current) {
										autolink(range, context);
										enterIsSendRef.current();
										return false;
									}
									return true;
								},
							},
							upToEdit: {
								key: "ArrowUp",
								empty: true,
								handler: (range: { index: number }) => {
									if (range.index > 0) return true;
									if (triggerRecentEditRef.current)
										triggerRecentEditRef.current();
								},
							},
							exitQuote: {
								key: "Backspace",
								empty: true,
								format: ["blockquote"],
								handler: () => {
									quill.format("blockquote", false);
									return false;
								},
							},
							enterQuote: {
								key: ">",
								empty: true,
								shiftKey: null,
								collapsed: true,
								format: {
									"code-block": false,
								},
								handler: () => {
									quill.format("blockquote", true);
									return false;
								},
							},
							exitCode: {
								key: "Backspace",
								empty: true,
								format: ["code-block"],
								handler: () => {
									quill.format("code-block", false);
									return false;
								},
							},
							enterCode: {
								key: "`",
								shiftKey: null,
								collapsed: true,
								prefix: /^``$/,
								format: {
									"code-block": false,
								},
								handler: (range: { index: number }) => {
									const [, offset] = quill.getLine(range.index);
									// Insert the text and cutoff history so that undo will get back
									// the raw value the user typed
									quill.insertText(range.index, "`", QuillInner.sources.USER);
									quill.history.cutoff();
									const delta = new Delta()
										.retain(range.index - offset) // Everything before this line
										.delete(3)
										.retain(1, { "code-block": true });
									quill.updateContents(delta, QuillInner.sources.USER);
									quill.history.cutoff();
									quill.setSelection(
										range.index - 2,
										QuillInner.sources.SILENT,
									);
									return false;
								},
							},
							"inline bold": {
								key: "*",
								shiftKey: null,
								collapsed: true,
								prefix: /\*[^*]+/,
								format: {
									"code-block": false,
								},
								handler: inlineFormat("*", /\*[^*]+/, "bold"),
							},
							"inline italic": {
								key: "_",
								shiftKey: null,
								collapsed: true,
								prefix: /_[^_]+/,
								format: {
									"code-block": false,
								},
								handler: inlineFormat("_", /_[^_]+/, "italic"),
							},
							"inline strike": {
								key: "~",
								shiftKey: null,
								collapsed: true,
								prefix: /~[^~]+/,
								format: {
									"code-block": false,
								},
								handler: inlineFormat("~", /~[^~]+/, "strike"),
							},
							"inline code": {
								key: "`",
								shiftKey: null,
								collapsed: true,
								prefix: /`[^`]+/,
								format: {
									"code-block": false,
								},
								handler: inlineFormat("`", /`[^`]+/, "code"),
							},
							"inline link": {
								key: [" ", "Enter", ">"],
								shiftKey: null,
								collapsed: true,
								prefix: /https:\/\/[^\s>]+/,
								format: {
									"code-block": false,
								},
								handler: (
									range: { index: number },
									context: { prefix: string; event: { key: string } },
								) => {
									// Insert the text and cutoff history so that undo will get back
									// the raw value the user typed
									quill.insertText(
										range.index,
										context.event.key === "Enter" ? "\n" : context.event.key,
										QuillInner.sources.USER,
									);
									quill.history.cutoff();

									autolink(range, context);
									return false;
								},
							},
						},
					},
					mention: {
						mentionDenotationChars: ["@", ":"],
						isolateCharacter: true,
						allowedChars: (mentionChar: string) => {
							if (mentionChar === ":") return /^[a-zA-Z0-9_)(/$*-]*$/;
							return null;
						},
						renderLoading: () => {
							const i = document.createElement("i");
							i.textContent = "Searching...";
							return i;
						},
						onClose: () => mentionCloseRef.current(),
						source: async function (
							searchTerm: string,
							renderList: (lst: unknown[]) => void,
							mentionChar: string,
						) {
							if (mentionChar === ":") {
								emojiMart.init({
									data: { ...(await import("@emoji-mart/data")) },
								});
								const emoticon =
									(await emojiMart.SearchIndex.search(":" + searchTerm)) ?? [];
								const results =
									(await emojiMart.SearchIndex.search(searchTerm)) ?? [];
								renderList(
									emoticon
										.concat(results)
										.map((emoji: typeof emojiMart.Data) => ({
											...emoji,
											value: `${emoji.skins[0]?.native} ${emoji.id}`,
										})),
								);
							} else if (mentionHandlerRef.current) {
								mentionHandlerRef.current(searchTerm, renderList, mentionChar);
							} else {
								renderList([]);
							}
						},
					},
				},
				placeholder,
				theme: "snow",
			});

			ref.current = quill;
			if (typeof fwdRef === "function") {
				fwdRef(quill);
			} else if (fwdRef) {
				fwdRef.current = quill;
			}

			if (defaultValueRef.current) {
				quill.setContents(defaultValueRef.current);
			}

			quill.on(QuillInner.events.TEXT_CHANGE, (...args) => {
				onTextChangeRef.current?.(...args);
			});

			quill.on(QuillInner.events.SELECTION_CHANGE, (...args) => {
				onSelectionChangeRef.current?.(...args);
			});

			return () => {
				ref.current = null;
				if (typeof fwdRef === "function") {
					fwdRef(null);
				} else if (fwdRef) {
					fwdRef.current = null;
				}
				container.innerHTML = "";
			};
		}, [fwdRef, placeholder]);

		return <form {...rest} ref={containerRef}></form>;
	},
);

Quill.displayName = "Quill";

interface MentionBlotData {
	id: string;
	value: string;
	denotationChar: string;
}

class MentionBlot extends Embed {
	static blotName = "mention";
	static tagName = "span";
	static className = "h-card";

	private hoverHandler?: EventListener;
	private clickHandler?: EventListener;

	constructor(scroll: ScrollBlot, node: Node) {
		super(scroll, node);
	}

	static render?(data: MentionBlotData): HTMLElement;

	static create(data: MentionBlotData) {
		if (data.denotationChar === ":") {
			// Not really a mention
			const span = document.createElement("span");
			span.innerText = emojiMart.Data.emojis[data.id].skins[0].native;
			return MentionBlot.setDataValues(span, data);
		}

		const node = super.create() as HTMLElement;

		if (typeof this.render === "function") {
			node.appendChild(this.render(data));
		} else {
			node.innerText = data.value;
		}

		return MentionBlot.setDataValues(node, data);
	}

	static setDataValues(element: HTMLElement, data: MentionBlotData) {
		const domNode = element;
		Object.keys(data).forEach((key) => {
			domNode.dataset[key] = data[key as keyof MentionBlotData];
		});
		return domNode;
	}

	static value(domNode: HTMLElement) {
		const data = domNode.dataset;
		if (!data.value) data.value = domNode.innerText;
		return data;
	}

	html() {
		const data = this.value();
		if (data.mention.denotationChar === ":") {
			return emojiMart.Data.emojis[data.mention.id].skins[0].native;
		}

		const node = document.createElement("span");
		node.className = "h-card";
		node.innerText = data.mention.value;
		return node.outerHTML;
	}
}

QuillInner.register({
	"blots/mention": MentionBlot,
	"modules/mention": Mention,
});
